// Mock services to simulate API calls for get-model-front with explanation features
import { mockModelListData, mockTreeData, mockHistoricalPredictions, mockOrganizations, mockUsers } from './mockData';
import API_CONFIG from '../config/apiConfig';

// Track prediction state for race condition simulation
const predictionState = {
  '176': {
    running: false,
    completed: false,
    predictionId: '39',
    startTime: null
  }
};

class MockAPI {
  // Simulate delay for API calls
  static delay(ms = 500) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Get model list (replaces Util.ajax.post('/train/getTrainRecord'))
  static async getTrainRecords() {
    await this.delay(800);

    return {
      data: {
        total: mockModelListData.length,
        list: mockModelListData
      }
    };
  }

  // Get list of models that support explanation
  static async getExplainableModels() {
    // In real mode, this should call the actual API
    if (!API_CONFIG.useMockData) {
      // In real mode, we should not be in this method at all
      // This is a fallback implementation
      return {
        success: true,
        data: []
      };
    }

    await this.delay(300);

    // Return a list of model names/IDs that support explanation
    // In a real implementation, this would come from the backend
    return {
      success: true,
      data: [
        { prefix: "2025-10-20" },
        { prefix: "AICO_fp1_7_10" },
        { prefix: "model_001" },
        { prefix: "model_004" },
        { prefix: "176" },
        { prefix: "299" },
        { prefix: "302" },
        { prefix: "310" },
        { prefix: "312" }
      ]
    };
  }

  // Submit training task
  static async submitTrainTask() {
    await this.delay(500);

    return {
      data: {
        message: 'Training task submitted successfully'
      }
    };
  }

  // User login
  static async userLogin() {
    await this.delay(300);

    // Check for environment variable to override current user
    let mockUser = mockUsers[0]; // default user
    if (process.env.REACT_APP_MOCK_USER_ID) {
      const userId = process.env.REACT_APP_MOCK_USER_ID;
      const user = mockUsers.find(u => u.id === userId);
      if (user) {
        mockUser = user;
      }
    }

    return {
      data: {
        hasLogin: false,
        token: 'mock_auth_token',
        userName: mockUser.userName,
        userId: mockUser.id,
        loginName: mockUser.loginName,
        roleList: '1',
        orgId: mockUser.orgId,
        trainState: '0'
      }
    };
  }

  // Get user info
  static async getUserInfo(userId) {
    await this.delay(300);

    // Check for environment variable to override current user
    let mockUser = mockUsers.find(u => u.id === userId) || mockUsers[0];
    if (process.env.REACT_APP_MOCK_USER_ID && !userId) {
      const envUserId = process.env.REACT_APP_MOCK_USER_ID;
      const user = mockUsers.find(u => u.id === envUserId);
      if (user) {
        mockUser = user;
      }
    }

    return {
      data: {
        roleList: ['1'], // Admin role
        userName: mockUser.userName,
        orgId: mockUser.orgId,
        userType: mockUser.userType
      }
    };
  }

  // Login out
  static async loginOut() {
    await this.delay(200);

    return {
      data: {
        message: 'Logged out successfully'
      }
    };
  }

  // Get decision tree data
  static async getModelTree(modelId, predictionId = null) {
    await this.delay(700);

    return {
      data: mockTreeData
    };
  }

  // Generate AI analysis
  static async generateAIAnalysis(modelId) {
    await this.delay(2000); // Simulate longer processing time

    return {
      data: {
        analysis: `## Model Analysis

This model demonstrates strong performance with an overall accuracy of 92%. The most important features for decision making are customer age (75% weight) and employment length (65% weight). The model shows a slight bias towards approving loans for older customers with longer employment histories. Consider reviewing the negative weight associated with income (-42%) to ensure this aligns with business objectives.

### Key Feature Analysis:
- **Age**: Contributed positively to the decision with a weight of 75%
- **Employment Length**: Strong positive indicator with a weight of 65%
- **Debt-to-Income Ratio**: Moderately influenced the decision with a weight of 51%

### Model Performance Metrics:
- **Accuracy**: 92%
- **Precision**: 89%
- **Recall**: 88%

### Recommendations:
1. Review the income threshold for potential adjustments
2. Consider additional factors for edge cases with similar profiles
3. Monitor for potential bias in age-based decisions`
      }
    };
  }

  // Publish local prediction task
  static async publishLocalPredictTask(trainId, fileName) {
    await this.delay(500);

    return {
      data: {
        taskId: `task_${trainId}`
      }
    };
  }

  // Get local prediction status
  static async getLocalPredict(trainId) {
    const prediction = predictionState[trainId];

    if (!prediction) {
      await this.delay(300);
      return {
        data: {
          predictStatus: 3,
          id: '99',
          taskId: trainId
        }
      };
    }

    // Start prediction if not running
    if (!prediction.running && !prediction.completed) {
      prediction.running = true;
      prediction.startTime = Date.now();
    }

    // Simulate prediction taking time (first call returns running)
    if (prediction.running && !prediction.completed) {
      await this.delay(300);
      return {
        data: {
          predictStatus: 2, // 2 = running
          taskId: trainId
        }
      };
    }

    // After delay, complete the prediction
    await this.delay(300);
    prediction.running = false;
    prediction.completed = true;

    return {
      data: {
        predictStatus: 3, // 3 = completed
        id: prediction.predictionId,
        taskId: trainId
      }
    };
  }

  // Kill local prediction task
  static async killLocalPredictTask(trainId) {
    await this.delay(300);

    return {
      data: {
        message: 'Task killed successfully'
      }
    };
  }

  // Get historical predictions
  static async getHistoricalPredictions(modelId) {
    await this.delay(600);

    // Filter historical predictions by model ID
    const filteredPredictions = mockHistoricalPredictions.filter(
      pred => pred.modelName === mockModelListData.find(m => m.id === modelId)?.projectName
    );

    return {
      data: {
        total: filteredPredictions.length,
        list: filteredPredictions
      }
    };
  }

  // Get configuration
  static async getConfig(type) {
    await this.delay(300);

    return {
      data: [
        {
          name: 'host',
          value: 'localhost'
        },
        {
          name: 'port',
          value: '3306'
        },
        {
          name: 'username',
          value: 'admin'
        },
        {
          name: 'password',
          value: 'password'
        }
      ]
    };
  }

  // Submit configuration
  static async submitConfig(type, configs) {
    await this.delay(500);

    return {
      data: {
        message: 'Configuration saved successfully'
      }
    };
  }

  // Test Hive data source
  static async testHiveDataSource(host, port) {
    await this.delay(800);

    return {
      data: {
        message: 'Connection test successful'
      }
    };
  }

  // Filter organization list
  static async filterOrgList(keyWords, current, pageSize) {
    await this.delay(300);

    // Filter organizations based on keyWords
    let filteredOrgs = mockOrganizations;
    if (keyWords) {
      filteredOrgs = mockOrganizations.filter(org =>
        org.name.toLowerCase().includes(keyWords.toLowerCase()) ||
        org.code.toLowerCase().includes(keyWords.toLowerCase())
      );
    }

    // Simulate pagination
    const start = (current - 1) * pageSize;
    const end = start + pageSize;
    const paginatedOrgs = filteredOrgs.slice(start, end);

    return {
      data: {
        total: filteredOrgs.length,
        list: paginatedOrgs
      }
    };
  }

  // Get organization info by ID
  static async getOrgInfoById(id) {
    await this.delay(300);

    return {
      data: {
        id: 'org_001',
        name: 'Mock Organization 1',
        sName: 'MO1',
        code: 'MO1',
        type: 'Type A',
        mainBusiness: 'Business 1',
        industry: 'Industry 1',
        country: 'Country 1',
        province: 'Province 1',
        city: 'City 1',
        district: 'District 1',
        address: 'Address 1',
        contactPerson: 'Contact 1',
        contactMobile: '123456789',
        contactPhone: '123456789',
        contactEmail: 'contact1@example.com'
      }
    };
  }

  // Create organization
  static async createOrg(orgData) {
    await this.delay(500);

    return {
      data: {
        message: 'Organization created successfully'
      }
    };
  }

  // Edit organization
  static async editOrg(orgData) {
    await this.delay(500);

    return {
      data: {
        message: 'Organization updated successfully'
      }
    };
  }

  // Filter user list
  static async filterUserList(keyWords, current, pageSize) {
    await this.delay(300);

    // Filter users based on keyWords
    let filteredUsers = mockUsers;
    if (keyWords) {
      filteredUsers = mockUsers.filter(user =>
        user.userName.toLowerCase().includes(keyWords.toLowerCase()) ||
        user.loginName.toLowerCase().includes(keyWords.toLowerCase())
      );
    }

    // Simulate pagination
    const start = (current - 1) * pageSize;
    const end = start + pageSize;
    const paginatedUsers = filteredUsers.slice(start, end);

    return {
      data: {
        total: filteredUsers.length,
        list: paginatedUsers
      }
    };
  }

  // Get user by ID
  static async getUserById(id) {
    await this.delay(300);

    return {
      data: {
        id: 'user_001',
        loginName: 'mockuser',
        userName: 'Mock User',
        orgId: 'org_001',
        userType: 'admin',
        mobile: '123456789',
        email: 'user@example.com'
      }
    };
  }

  // Get current user organization
  static async getCurrentUserOrg() {
    await this.delay(300);

    // Get current user's orgId from localStorage or environment variable
    let currentUserOrgId = 'org_001'; // default organization

    // Check for environment variable to override current user
    if (process.env.REACT_APP_MOCK_USER_ID) {
      const mockUserId = process.env.REACT_APP_MOCK_USER_ID;
      const mockUser = mockUsers.find(user => user.id === mockUserId);
      if (mockUser) {
        currentUserOrgId = mockUser.orgId;
      }
    } else if (localStorage.authOrgId) {
      currentUserOrgId = localStorage.authOrgId;
    }

    // Find organization
    const org = mockOrganizations.find(o => o.id === currentUserOrgId) || mockOrganizations[0];

    return {
      data: {
        id: org.id,
        name: org.name,
        code: org.code
      }
    };
  }

  // Get organization users
  static async getOrgUsers(keyWords, current, pageSize) {
    await this.delay(300);

    // Get current user's orgId from localStorage or environment variable
    let currentUserOrgId = 'org_001'; // default organization

    // Check for environment variable to override current user
    if (process.env.REACT_APP_MOCK_USER_ID) {
      const mockUserId = process.env.REACT_APP_MOCK_USER_ID;
      const mockUser = mockUsers.find(user => user.id === mockUserId);
      if (mockUser) {
        currentUserOrgId = mockUser.orgId;
      }
    } else if (localStorage.authOrgId) {
      currentUserOrgId = localStorage.authOrgId;
    }

    // Filter users by organization
    let filteredUsers = mockUsers.filter(user => user.orgId === currentUserOrgId);

    // Filter users based on keyWords
    if (keyWords) {
      filteredUsers = filteredUsers.filter(user =>
        user.userName.toLowerCase().includes(keyWords.toLowerCase()) ||
        user.loginName.toLowerCase().includes(keyWords.toLowerCase())
      );
    }

    // Simulate pagination
    const start = (current - 1) * pageSize;
    const end = start + pageSize;
    const paginatedUsers = filteredUsers.slice(start, end);

    return {
      data: {
        total: filteredUsers.length,
        list: paginatedUsers
      }
    };
  }

  // Create user
  static async createUser(userData) {
    await this.delay(500);

    return {
      data: {
        message: 'User created successfully',
        userId: 'user_' + Math.floor(Math.random() * 1000)
      }
    };
  }

  // Update user
  static async updateUser(userData) {
    await this.delay(500);

    return {
      data: {
        message: 'User updated successfully'
      }
    };
  }

  // Delete user
  static async deleteUser(userId) {
    await this.delay(500);

    return {
      data: {
        message: 'User deleted successfully'
      }
    };
  }

  // Update organization
  static async updateOrg(orgData) {
    await this.delay(500);

    return {
      data: {
        message: 'Organization updated successfully'
      }
    };
  }

  // Delete organization
  static async deleteOrg(orgId) {
    await this.delay(500);

    return {
      data: {
        message: 'Organization deleted successfully'
      }
    };
  }

  // Filter role list
  static async filterRoleList(keyWords, current, pageSize) {
    await this.delay(300);

    const mockRoleList = [
      {
        id: 'role_001',
        name: 'Admin',
        code: 'ADMIN',
        remark: 'Administrator role'
      }
    ];

    return {
      data: {
        total: mockRoleList.length,
        list: mockRoleList
      }
    };
  }

  // Create role
  static async createRole(roleData) {
    await this.delay(500);

    return {
      data: {
        message: 'Role created successfully'
      }
    };
  }

  // Edit role
  static async editRole(roleData) {
    await this.delay(500);

    return {
      data: {
        message: 'Role updated successfully'
      }
    };
  }

  // Filter privilege list
  static async filterPrivilegeList(keyWords, current, pageSize) {
    await this.delay(300);

    const mockPrivilegeList = [
      {
        id: 'priv_001',
        name: 'View Models',
        code: 'VIEW_MODELS',
        menuId: 'menu_001',
        menuName: 'Models',
        remark: 'Permission to view models'
      }
    ];

    return {
      data: {
        total: mockPrivilegeList.length,
        list: mockPrivilegeList
      }
    };
  }

  // Get privilege by ID
  static async getPrivilegeById(id) {
    await this.delay(300);

    return {
      data: {
        id: 'priv_001',
        name: 'View Models',
        code: 'VIEW_MODELS',
        menuId: 'menu_001',
        remark: 'Permission to view models'
      }
    };
  }

  // Create privilege
  static async createPrivilege(privilegeData) {
    await this.delay(500);

    return {
      data: {
        message: 'Privilege created successfully'
      }
    };
  }

  // Edit privilege
  static async editPrivilege(privilegeData) {
    await this.delay(500);

    return {
      data: {
        message: 'Privilege updated successfully'
      }
    };
  }

  // Filter menu list
  static async filterMenuList(keyWords, current, pageSize) {
    await this.delay(300);

    const mockMenuList = [
      {
        id: 'menu_001',
        name: 'Models',
        code: 'MODELS',
        parentId: '',
        parentName: '',
        url: '/models',
        orderNum: 1,
        menuType: 'menu',
        visible: '1',
        perms: 'model:view',
        icon: 'model'
      }
    ];

    return {
      data: {
        total: mockMenuList.length,
        list: mockMenuList
      }
    };
  }

  // Get menu by ID
  static async getMenuById(id) {
    await this.delay(300);

    return {
      data: {
        id: 'menu_001',
        name: 'Models',
        code: 'MODELS',
        parentId: '',
        url: '/models',
        orderNum: 1,
        menuType: 'menu',
        visible: '1',
        perms: 'model:view',
        icon: 'model'
      }
    };
  }

  // Create menu
  static async createMenu(menuData) {
    await this.delay(500);

    return {
      data: {
        message: 'Menu created successfully'
      }
    };
  }

  // Edit menu
  static async editMenu(menuData) {
    await this.delay(500);

    return {
      data: {
        message: 'Menu updated successfully'
      }
    };
  }
}

// Real API implementation
class RealAPI {
  static async request(method, url, data = null) {
    // Use the full url as provided - no prefixing needed since axios is already configured with the correct baseURL
    // Handle URLs with different prefixes appropriately
    let fullUrl = url;

    // If the URL doesn't start with a prefix and doesn't start with http, add /get prefix
    // Special handling for /models endpoint - it should not have /get prefix
    if (!url.startsWith('http') &&
      !url.startsWith('/get/') &&
      !url.startsWith('/explain/') &&
      !url.startsWith('/api/') &&
      url !== '/models') {
      // Add /get prefix for URLs without prefixes
      fullUrl = '/get' + (url.startsWith('/') ? url : '/' + url);
    }

    const options = {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
        'token': localStorage.getItem('authToken') || '',
        'orgId': localStorage.getItem('authOrgId') || '',
        'X-Tenant-ID': localStorage.getItem('authOrgId') || ''
      }
    };

    if (data && (method === 'POST' || method === 'PUT')) {
      options.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(fullUrl, options);

      // Handle 401 Unauthorized - token expired
      if (response.status === 401) {
        // Try to parse the response to check if it's a token expiration
        try {
          const errorData = await response.json();
          if (errorData.code === '401' || errorData.message === 'token expired') {
            console.error('Token expired, redirecting to login...');
            // Clear auth data
            localStorage.removeItem('authToken');
            localStorage.removeItem('authUserId');
            localStorage.removeItem('authUserName');
            localStorage.removeItem('authRoleList');
            localStorage.removeItem('authOrgId');
            // Redirect to login with return URL
            window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
            throw new Error('Token expired');
          }
        } catch (jsonError) {
          // If JSON parsing fails, still treat as 401
          console.error('401 Unauthorized - redirecting to login...');
          localStorage.removeItem('authToken');
          localStorage.removeItem('authUserId');
          localStorage.removeItem('authUserName');
          localStorage.removeItem('authRoleList');
          localStorage.removeItem('authOrgId');
          window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
          throw new Error('Unauthorized');
        }
      }

      // Handle 412 - logged in elsewhere
      if (response.status === 412) {
        console.error('Account logged in elsewhere, redirecting to login...');
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUserId');
        localStorage.removeItem('authUserName');
        localStorage.removeItem('authRoleList');
        localStorage.removeItem('authOrgId');
        window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
        throw new Error('Account logged in elsewhere');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${method} ${url}`, error);
      throw error;
    }
  }

  static async getTrainRecords(params) {
    return await this.request('POST', '/train/getTrainRecord', params);
  }

  static async submitTrainTask(params) {
    return await this.request('POST', '/train/submitTrainTask', params);
  }

  static async userLogin(params) {
    return await this.request('POST', '/common/login', params);
  }

  static async getUserInfo(userId) {
    return await this.request('GET', `/common/user/${userId}`);
  }

  static async loginOut() {
    return await this.request('POST', '/common/loginOut');
  }

  static async getModelTree(modelId, predictionId = null) {
    if (predictionId) {
      // This is a prediction explanation - use prediction-specific API path
      return await this.request('GET', `/explain/model/${modelId}/prediction/${predictionId}/tree`);
    } else {
      // This is a model explanation - use standard API path
      return await this.request('GET', `/explain/model/${modelId}/tree`);
    }
  }

  static async getExplainableModels() {
    return await this.request('GET', '/models');
  }

  static async generateAIAnalysis(modelId) {
    return await this.request('POST', `/explain/model/${modelId}/generateAIAnalysis`);
  }

  static async publishLocalPredictTask(trainId, fileName) {
    return await this.request('POST', '/predict/publishLocalPredictTask', { trainId, fileName });
  }

  static async getLocalPredict(taskId) {
    return await this.request('GET', `/predict/getLocalPredict/${taskId}`);
  }

  static async killLocalPredictTask(trainId) {
    return await this.request('GET', `/predict/killLocalPredictTask?trainId=${trainId}`);
  }

  static async getHistoricalPredictions(modelId) {
    return await this.request('GET', `/predict/historical/${modelId}/list`);
  }

  static async getConfig(type) {
    return await this.request('GET', `/config/getConfig?type=${type}`);
  }

  static async submitConfig(type, configs) {
    return await this.request('POST', '/config/submitConfig', { type, configs });
  }

  static async testHiveDataSource(host, port) {
    return await this.request('GET', `/config/testHiveDataSource?host=${host}&port=${port}`);
  }

  static async filterOrgList(keyWords, current, pageSize) {
    return await this.request('GET', `/admin/filterOrgList?keyWords=${keyWords}&current=${current}&pageSize=${pageSize}`);
  }

  static async getOrgInfoById(id) {
    return await this.request('GET', `/admin/getOrgInfoById?id=${id}`);
  }

  static async createOrg(orgData) {
    return await this.request('POST', '/admin/createOrg', orgData);
  }

  static async editOrg(orgData) {
    return await this.request('POST', '/admin/editOrg', orgData);
  }

  static async filterUserList(keyWords, current, pageSize) {
    return await this.request('GET', `/admin/filterUserList?keyWords=${keyWords}&current=${current}&pageSize=${pageSize}`);
  }

  static async getUserById(id) {
    return await this.request('GET', `/admin/getUserById?id=${id}`);
  }

  static async createUser(userData) {
    return await this.request('POST', '/admin/createUser', userData);
  }

  static async editUser(userData) {
    return await this.request('POST', '/admin/editUser', userData);
  }

  static async filterRoleList(keyWords, current, pageSize) {
    return await this.request('GET', `/admin/filterRoleList?keyWords=${keyWords}&current=${current}&pageSize=${pageSize}`);
  }

  static async createRole(roleData) {
    return await this.request('POST', '/admin/createRole', roleData);
  }

  static async editRole(roleData) {
    return await this.request('POST', '/admin/editRole', roleData);
  }

  static async filterPrivilegeList(keyWords, current, pageSize) {
    return await this.request('GET', `/admin/filterPrivilegeList?keyWords=${keyWords}&current=${current}&pageSize=${pageSize}`);
  }

  static async getPrivilegeById(id) {
    return await this.request('GET', `/admin/getPrivilegeById?id=${id}`);
  }

  static async createPrivilege(privilegeData) {
    return await this.request('POST', '/admin/createPrivilege', privilegeData);
  }

  static async editPrivilege(privilegeData) {
    return await this.request('POST', '/admin/editPrivilege', privilegeData);
  }

  static async filterMenuList(keyWords, current, pageSize) {
    return await this.request('GET', `/admin/filterMenuList?keyWords=${keyWords}&current=${current}&pageSize=${pageSize}`);
  }

  static async getMenuById(id) {
    return await this.request('GET', `/admin/getMenuById?id=${id}`);
  }

  static async createMenu(menuData) {
    return await this.request('POST', '/admin/createMenu', menuData);
  }

  static async editMenu(menuData) {
    return await this.request('POST', '/admin/editMenu', menuData);
  }

  static async getPredictionCases(modelId, predictionId) {
    return await this.request('GET', `/explain/model/${modelId}/prediction/${predictionId}/cases`);
  }

  static async getModelCases(modelId) {
    return await this.request('GET', `/explain/model/${modelId}/cases`);
  }

  static async getPredictionTreeSample(modelId, predictionId, caseId) {
    return await this.request('GET', `/explain/model/${modelId}/prediction/${predictionId}/tree/sample/${caseId}`);
  }

  static async getModelTreeSample(modelId, caseId) {
    return await this.request('GET', `/explain/model/${modelId}/tree/sample/${caseId}`);
  }
  
  static async getExplainablePredictions(modelId) {
    return await this.request('GET', `/explain/model/${modelId}/predictions`);
  }

}

// Unified API service that switches between mock and real implementations
class APIService {
  static async getTrainRecords(params) {
    return API_CONFIG.useMockData
      ? MockAPI.getTrainRecords(params)
      : RealAPI.getTrainRecords(params);
  }

  static async getExplainableModels() {
    return API_CONFIG.useMockData
      ? MockAPI.getExplainableModels()
      : RealAPI.getExplainableModels();
  }

  static async submitTrainTask(params) {
    return API_CONFIG.useMockData
      ? MockAPI.submitTrainTask(params)
      : RealAPI.submitTrainTask(params);
  }

  static async userLogin(params) {
    return API_CONFIG.useMockData
      ? MockAPI.userLogin(params)
      : RealAPI.userLogin(params);
  }

  static async getUserInfo(userId) {
    return API_CONFIG.useMockData
      ? MockAPI.getUserInfo(userId)
      : RealAPI.getUserInfo(userId);
  }

  static async loginOut() {
    return API_CONFIG.useMockData
      ? MockAPI.loginOut()
      : RealAPI.loginOut();
  }

  static async getModelTree(modelId, predictionId = null) {
    return API_CONFIG.useMockData
      ? MockAPI.getModelTree(modelId, predictionId)
      : RealAPI.getModelTree(modelId, predictionId);
  }

  static async generateAIAnalysis(modelId) {
    return API_CONFIG.useMockData
      ? MockAPI.generateAIAnalysis(modelId)
      : RealAPI.generateAIAnalysis(modelId);
  }

  static async publishLocalPredictTask(trainId, fileName) {
    return API_CONFIG.useMockData
      ? MockAPI.publishLocalPredictTask(trainId, fileName)
      : RealAPI.publishLocalPredictTask(trainId, fileName);
  }

  static async getLocalPredict(taskId) {
    return API_CONFIG.useMockData
      ? MockAPI.getLocalPredict(taskId)
      : RealAPI.getLocalPredict(taskId);
  }

  static async killLocalPredictTask(trainId) {
    return API_CONFIG.useMockData
      ? MockAPI.killLocalPredictTask(trainId)
      : RealAPI.killLocalPredictTask(trainId);
  }

  static async getHistoricalPredictions(modelId) {
    return API_CONFIG.useMockData
      ? MockAPI.getHistoricalPredictions(modelId)
      : RealAPI.getHistoricalPredictions(modelId);
  }

  static async getConfig(type) {
    return API_CONFIG.useMockData
      ? MockAPI.getConfig(type)
      : RealAPI.getConfig(type);
  }

  static async submitConfig(type, configs) {
    return API_CONFIG.useMockData
      ? MockAPI.submitConfig(type, configs)
      : RealAPI.submitConfig(type, configs);
  }

  static async testHiveDataSource(host, port) {
    return API_CONFIG.useMockData
      ? MockAPI.testHiveDataSource(host, port)
      : RealAPI.testHiveDataSource(host, port);
  }

  static async filterOrgList(keyWords, current, pageSize) {
    return API_CONFIG.useMockData
      ? MockAPI.filterOrgList(keyWords, current, pageSize)
      : RealAPI.filterOrgList(keyWords, current, pageSize);
  }

  static async getOrgInfoById(id) {
    return API_CONFIG.useMockData
      ? MockAPI.getOrgInfoById(id)
      : RealAPI.getOrgInfoById(id);
  }

  static async createOrg(orgData) {
    return API_CONFIG.useMockData
      ? MockAPI.createOrg(orgData)
      : RealAPI.createOrg(orgData);
  }

  static async editOrg(orgData) {
    return API_CONFIG.useMockData
      ? MockAPI.editOrg(orgData)
      : RealAPI.editOrg(orgData);
  }

  static async filterUserList(keyWords, current, pageSize) {
    return API_CONFIG.useMockData
      ? MockAPI.filterUserList(keyWords, current, pageSize)
      : RealAPI.filterUserList(keyWords, current, pageSize);
  }

  static async getUserById(id) {
    return API_CONFIG.useMockData
      ? MockAPI.getUserById(id)
      : RealAPI.getUserById(id);
  }

  static async createUser(userData) {
    return API_CONFIG.useMockData
      ? MockAPI.createUser(userData)
      : RealAPI.createUser(userData);
  }

  static async editUser(userData) {
    return API_CONFIG.useMockData
      ? MockAPI.editUser(userData)
      : RealAPI.editUser(userData);
  }

  static async getCurrentUserOrg() {
    return API_CONFIG.useMockData
      ? MockAPI.getCurrentUserOrg()
      : RealAPI.getCurrentUserOrg();
  }

  static async filterRoleList(keyWords, current, pageSize) {
    return API_CONFIG.useMockData
      ? MockAPI.filterRoleList(keyWords, current, pageSize)
      : RealAPI.filterRoleList(keyWords, current, pageSize);
  }

  static async createRole(roleData) {
    return API_CONFIG.useMockData
      ? MockAPI.createRole(roleData)
      : RealAPI.createRole(roleData);
  }

  static async editRole(roleData) {
    return API_CONFIG.useMockData
      ? MockAPI.editRole(roleData)
      : RealAPI.editRole(roleData);
  }

  static async filterPrivilegeList(keyWords, current, pageSize) {
    return API_CONFIG.useMockData
      ? MockAPI.filterPrivilegeList(keyWords, current, pageSize)
      : RealAPI.filterPrivilegeList(keyWords, current, pageSize);
  }

  static async getPrivilegeById(id) {
    return API_CONFIG.useMockData
      ? MockAPI.getPrivilegeById(id)
      : RealAPI.getPrivilegeById(id);
  }

  static async createPrivilege(privilegeData) {
    return API_CONFIG.useMockData
      ? MockAPI.createPrivilege(privilegeData)
      : RealAPI.createPrivilege(privilegeData);
  }

  static async editPrivilege(privilegeData) {
    return API_CONFIG.useMockData
      ? MockAPI.editPrivilege(privilegeData)
      : RealAPI.editPrivilege(privilegeData);
  }

  static async filterMenuList(keyWords, current, pageSize) {
    return API_CONFIG.useMockData
      ? MockAPI.filterMenuList(keyWords, current, pageSize)
      : RealAPI.filterMenuList(keyWords, current, pageSize);
  }

  static async getMenuById(id) {
    return API_CONFIG.useMockData
      ? MockAPI.getMenuById(id)
      : RealAPI.getMenuById(id);
  }

  static async createMenu(menuData) {
    return API_CONFIG.useMockData
      ? MockAPI.createMenu(menuData)
      : RealAPI.createMenu(menuData);
  }

  static async editMenu(menuData) {
    return API_CONFIG.useMockData
      ? MockAPI.editMenu(menuData)
      : RealAPI.editMenu(menuData);
  }

  static async getOrgUsers(keyWords, current, pageSize) {
    return API_CONFIG.useMockData
      ? MockAPI.getOrgUsers(keyWords, current, pageSize)
      : RealAPI.getOrgUsers(keyWords, current, pageSize);
  }
  
  static async getExplainablePredictions(modelId) {
    return API_CONFIG.useMockData
      ? MockAPI.getExplainablePredictions(modelId)
      : RealAPI.getExplainablePredictions(modelId);
  }
}

// Replace Util.ajax calls with mock API calls
const apiUtil = {
  ajax: {
    post: async (url, data) => {
      console.log('Mock API POST request:', url, data);
      // Always proxy /explain/ requests to the real backend, even in mock
      // mode, so the explainer (tree/overview/AI-analysis) uses live data.
      if (url.startsWith('/explain/')) {
        return await RealAPI.request('POST', url, data);
      }
      // When not using mock data, use the full URL as provided
      // Only normalize URLs when using mock data
      // If URL already starts with /get/, /explain/, or /api/, use as is
      // For URLs starting with /train/ or /predict/, use as is (no /get prefix)
      // Otherwise, add /get prefix for backward compatibility
      let normalizedUrl = url;
      if (API_CONFIG.useMockData) {
        if (url.startsWith('/get/') || url.startsWith('/explain/') || url.startsWith('/api/')) {
          // URL already has a prefix, remove the first part for mock matching
          const parts = url.split('/');
          parts.splice(1, 1); // Remove the prefix part (get, explain, or api)
          normalizedUrl = parts.join('/');
          if (normalizedUrl === '') normalizedUrl = '/';
        } else if (url.startsWith('/train/') || url.startsWith('/predict/')) {
          // URLs starting with /train/ or /predict/ should not have /get prefix added
          normalizedUrl = url;
        } else {
          // Add /get prefix for URLs without prefixes
          normalizedUrl = '/get' + (url.startsWith('/') ? url : '/' + url);
        }
      }
      console.log('Normalized URL:', normalizedUrl);

      if (API_CONFIG.useMockData) {
        switch (normalizedUrl) {
          case '/train/getTrainRecord':
            return APIService.getTrainRecords(data);
          case '/train/submitTrainTask':
            return APIService.submitTrainTask(data);
          case '/predict/getLocalPredictList':
            const pageNum = data && data.pageNum ? parseInt(data.pageNum) : 1;
            const pageSize = data && data.pageSize ? parseInt(data.pageSize) : 10;
            const trainId = data && data.trainId;

            let filteredPredictions = [...mockHistoricalPredictions];

            if (trainId && predictionState[trainId]) {
              const prediction = predictionState[trainId];
              const model = mockModelListData.find(m => m.id === trainId);

              if (model && prediction.completed) {
                const newPrediction = {
                  id: prediction.predictionId,
                  modelName: model.projectName,
                  taskId: `task_${trainId}_${prediction.predictionId}`,
                  fileName: 'customer_data_v3.csv',
                  createTime: '2025/03/15 11:30:00',
                  status: 3,
                  resultPath: `/get/predict/download/localPredict/task_${trainId}_${prediction.predictionId}`,
                  reportPath: `/get/predict/download/localReport/task_${trainId}_${prediction.predictionId}`,
                  predictFilePath: '/path/to/predict_file.csv'
                };

                if (!filteredPredictions.find(p => p.id === prediction.predictionId)) {
                  filteredPredictions.unshift(newPrediction);
                }
              } else if (model) {
                filteredPredictions = filteredPredictions.filter(pred => pred.modelName === model.projectName);
              }
            } else if (trainId) {
              const model = mockModelListData.find(m => m.id === trainId);
              if (model) {
                filteredPredictions = filteredPredictions.filter(pred => pred.modelName === model.projectName);
              }
            }

            const start = (pageNum - 1) * pageSize;
            const end = start + pageSize;
            const paginatedPredictions = filteredPredictions.slice(start, end);

            await MockAPI.delay(500);
            const result = {
              success: true,
              data: {
                total: filteredPredictions.length,
                list: paginatedPredictions,
                pageNum: pageNum,
                pageSize: pageSize
              }
            };
            return result;
          case '/predict/publishLocalPredictTask':
            return APIService.publishLocalPredictTask(data.trainId, data.fileName);
          case '/common/login':
            return APIService.userLogin(data);
          case '/common/loginOut':
            return APIService.loginOut();
          case '/config/submitConfig':
            return APIService.submitConfig(data.type, data.configs);
          case '/admin/createOrg':
            return APIService.createOrg(data);
          case '/admin/editOrg':
            return APIService.editOrg(data);
          case '/admin/createUser':
            return APIService.createUser(data);
          case '/admin/editUser':
            return APIService.editUser(data);
          case '/admin/deleteUser':
            return APIService.deleteUser(data.id);
          case '/admin/createRole':
            return APIService.createRole(data);
          case '/admin/editRole':
            return APIService.editRole(data);
          case '/admin/createPrivilege':
            return APIService.createPrivilege(data);
          case '/admin/editPrivilege':
            return APIService.editPrivilege(data);
          case '/admin/createMenu':
            return APIService.createMenu(data);
          case '/admin/editMenu':
            return APIService.editMenu(data);
          case '/org/create':
            return APIService.createOrg(data);
          case '/org/update':
            return APIService.updateOrg(data);
          case '/org/delete':
            return APIService.deleteOrg(data.id);
          case '/user/create':
            return APIService.createUser(data);
          case '/user/update':
            return APIService.updateUser(data);
          case '/user/delete':
            return APIService.deleteUser(data.id);
          default:
            console.log('No specific mock handler for URL, using default behavior');
            await MockAPI.delay(200);
            return { data: { message: 'Success' } };
        }
      } else {
        // When not using mock data, make real API calls with the original URL
        return await RealAPI.request('POST', url, data);
      }
    },

    get: async (url, params) => {
      console.log('Mock API GET request:', url, params);
      // Always proxy /explain/ requests to the real backend, even in mock
      // mode, so the explainer (tree/overview/cases) uses live data.
      if (url.startsWith('/explain/')) {
        return await RealAPI.request('GET', url, params);
      }
      // When not using mock data, use the full URL as provided
      // Only normalize URLs when using mock data
      // If URL already starts with /get/, /explain/, or /api/, use as is
      // For URLs starting with /train/ or /predict/, use as is (no /get prefix)
      // Otherwise, add /get prefix for backward compatibility
      let normalizedUrl = url;
      if (API_CONFIG.useMockData) {
        if (url.startsWith('/get/') || url.startsWith('/explain/') || url.startsWith('/api/')) {
          // URL already has a prefix, remove the first part for mock matching
          const parts = url.split('/');
          parts.splice(1, 1); // Remove the prefix part (get, explain, or api)
          normalizedUrl = parts.join('/');
          if (normalizedUrl === '') normalizedUrl = '/';
        } else if (url.startsWith('/train/') || url.startsWith('/predict/')) {
          // URLs starting with /train/ or /predict/ should not have /get prefix added
          normalizedUrl = url;
        } else {
          // Add /get prefix for URLs without prefixes
          normalizedUrl = '/get' + (url.startsWith('/') ? url : '/' + url);
        }
      }
      console.log('Normalized URL:', normalizedUrl);
      console.log('Full URL processing for:', url, 'params:', params);

      if (API_CONFIG.useMockData) {
        if (normalizedUrl === '/get/models' || normalizedUrl === '/models') {
          return APIService.getExplainableModels();
        }

        if (normalizedUrl.includes('/predict/getLocalPredict/')) {
          const id = normalizedUrl.split('/').pop();
          return APIService.getLocalPredict(id);
        }

        if (normalizedUrl.includes('/predict/killLocalPredictTask')) {
          return APIService.killLocalPredictTask();
        }

        if (normalizedUrl.startsWith('/model/') && normalizedUrl.endsWith('/tree')) {
          // Check if this is a prediction-specific explanation
          if (normalizedUrl.includes('/prediction/')) {
            // Extract modelId and predictionId
            const parts = normalizedUrl.split('/');
            const modelId = parts[2];
            const predictionId = parts[4];
            return APIService.getModelTree(modelId, predictionId);
          } else {
            // Standard model explanation
            const parts = normalizedUrl.split('/');
            const modelId = parts[2];
            return APIService.getModelTree(modelId);
          }
        }

        // Handle new prediction explanation paths
        if (normalizedUrl.includes('/model/') && normalizedUrl.includes('/prediction/') && normalizedUrl.endsWith('/cases')) {
          // Extract modelId and predictionId from the path
          const parts = normalizedUrl.split('/');
          const modelId = parts[2];
          const predictionId = parts[4];
          console.log('Handling explainable prediction cases for modelId:', modelId, 'predictionId:', predictionId);
          // Return mock case IDs
          await MockAPI.delay(500);
          return {
            success: true,
            data: [
              'case_001', 'case_002', 'case_003', 'case_004', 'case_005',
              'case_006', 'case_007', 'case_008', 'case_009', 'case_010'
            ]
          };
        }

        if (normalizedUrl.includes('/model/') && normalizedUrl.includes('/prediction/') && normalizedUrl.includes('/tree/sample/')) {
          // Extract modelId, predictionId and caseId from the path
          const parts = normalizedUrl.split('/');
          const modelId = parts[2];
          const predictionId = parts[4];
          const caseId = parts[6];
          console.log('Handling explainable prediction tree sample for modelId:', modelId, 'predictionId:', predictionId, 'caseId:', caseId);
          // Return mock tree data
          await MockAPI.delay(700);
          return {
            success: true,
            data: mockTreeData
          };
        }

        // Handle standard model cases
        if (normalizedUrl.includes('/model/') && normalizedUrl.endsWith('/cases') && !normalizedUrl.includes('/prediction/')) {
          // Extract modelId from the path
          const parts = normalizedUrl.split('/');
          const modelId = parts[2];
          console.log('Handling model cases for modelId:', modelId);
          // Return mock case IDs
          await MockAPI.delay(500);
          return {
            success: true,
            data: [
              'case_001', 'case_002', 'case_003', 'case_004', 'case_005',
              'case_006', 'case_007', 'case_008', 'case_009', 'case_010'
            ]
          };
        }

        // Handle standard model tree sample
        if (normalizedUrl.includes('/model/') && normalizedUrl.includes('/tree/sample/')) {
          // Extract modelId and caseId from the path
          const parts = normalizedUrl.split('/');
          const modelId = parts[2];
          const caseId = parts[4];
          console.log('Handling model tree sample for modelId:', modelId, 'caseId:', caseId);
          // Return mock tree data
          await MockAPI.delay(700);
          return {
            success: true,
            data: mockTreeData
          };
        }

        if (normalizedUrl.includes('/predict/historical/') && normalizedUrl.endsWith('/list')) {
          const parts = normalizedUrl.split('/');
          const modelId = parts[parts.length - 2];
          return APIService.getHistoricalPredictions(modelId);
        }

        // Handle individual case detail
        if (normalizedUrl.includes('/model/') && normalizedUrl.includes('/case/')) {
          // Extract caseId from the path
          const parts = normalizedUrl.split('/');
          const caseId = parts[parts.indexOf('case') + 1];

          // Return mock case data
          await MockAPI.delay(500);
          return {
            success: true,
            data: {
              id: caseId,
              prediction: Math.random() > 0.5 ? 'Approved (U)' : 'Denied (D)',
              individual_features: {
                age: Math.floor(Math.random() * 50) + 20,
                income: Math.floor(Math.random() * 100000) + 30000,
                credit_score: Math.floor(Math.random() * 300) + 500,
                employment_length: Math.floor(Math.random() * 20) + 1,
                debt_to_income: (Math.random() * 0.5).toFixed(4),
                loan_amount: Math.floor(Math.random() * 50000) + 10000
              }
            }
          };
        }
        
        // Handle explainable predictions for a model
        if (normalizedUrl.includes('/model/') && normalizedUrl.endsWith('/predictions')) {
          console.log('Handling /model/*/predictions request for normalized URL:', normalizedUrl);
          const parts = normalizedUrl.split('/');
          const modelId = parts[2];

          console.log('Extracted modelId:', modelId);

          let explainablePreds = ['1', '2', '5', '7', '10', '15', '20'];
          if (modelId === '176') {
            explainablePreds = ['39'];
            await MockAPI.delay(2000);
          } else {
            await MockAPI.delay(300);
          }

          const result = {
            success: true,
            data: explainablePreds
          };
          console.log('Returning result for explainable predictions:', result);
          return result;
        }

        if (normalizedUrl.startsWith('/common/user/')) {
          const userId = normalizedUrl.split('/').pop();
          return APIService.getUserInfo(userId);
        }

        if (normalizedUrl.startsWith('/config/getConfig')) {
          const type = params && params.type;
          return APIService.getConfig(type);
        }

        if (normalizedUrl.startsWith('/config/testHiveDataSource')) {
          const host = params && params.host;
          const port = params && params.port;
          return APIService.testHiveDataSource(host, port);
        }

        if (normalizedUrl.startsWith('/admin/filterOrgList')) {
          const keyWords = params && params.keyWords;
          const current = params && params.current;
          const pageSize = params && params.pageSize;
          return APIService.filterOrgList(keyWords, current, pageSize);
        }

        if (normalizedUrl.startsWith('/org/list')) {
          const keyWords = params && params.keyWords;
          const current = params && params.current;
          const pageSize = params && params.pageSize;
          return APIService.filterOrgList(keyWords, current, pageSize);
        }

        if (normalizedUrl.startsWith('/user/org')) {
          return APIService.getCurrentUserOrg();
        }

        if (normalizedUrl.startsWith('/user/org/users')) {
          const keyWords = params && params.keyWords;
          const current = params && params.current;
          const pageSize = params && params.pageSize;
          return APIService.getOrgUsers(keyWords, current, pageSize);
        }

        if (normalizedUrl.startsWith('/admin/getOrgInfoById')) {
          const id = params && params.id;
          return APIService.getOrgInfoById(id);
        }

        if (normalizedUrl.startsWith('/admin/filterUserList')) {
          const keyWords = params && params.keyWords;
          const current = params && params.current;
          const pageSize = params && params.pageSize;
          return APIService.filterUserList(keyWords, current, pageSize);
        }

        if (normalizedUrl.startsWith('/admin/getUserById')) {
          const id = params && params.id;
          return APIService.getUserById(id);
        }

        if (normalizedUrl.startsWith('/admin/filterRoleList')) {
          const keyWords = params && params.keyWords;
          const current = params && params.current;
          const pageSize = params && params.pageSize;
          return APIService.filterRoleList(keyWords, current, pageSize);
        }

        if (normalizedUrl.startsWith('/admin/filterPrivilegeList')) {
          const keyWords = params && params.keyWords;
          const current = params && params.current;
          const pageSize = params && params.pageSize;
          return APIService.filterPrivilegeList(keyWords, current, pageSize);
        }

        if (normalizedUrl.startsWith('/admin/getPrivilegeById')) {
          const id = params && params.id;
          return APIService.getPrivilegeById(id);
        }

        if (normalizedUrl.startsWith('/admin/filterMenuList')) {
          const keyWords = params && params.keyWords;
          const current = params && params.current;
          const pageSize = params && params.pageSize;
          return APIService.filterMenuList(keyWords, current, pageSize);
        }

        if (normalizedUrl.startsWith('/admin/getMenuById')) {
          const id = params && params.id;
          return APIService.getMenuById(id);
        }
        
        // Handle train record list
        if (normalizedUrl.includes('/train/getTrainRecord')) {
          console.log('Handling /train/getTrainRecord request with params:', params);
          // Extract parameters
          const database = params && params.database;
          const type = params && params.type;
          const pageNum = params && params.pageNum ? parseInt(params.pageNum) : 1;
          const pageSize = params && params.pageSize ? parseInt(params.pageSize) : 10;
          
          console.log('Extracted parameters - database:', database, 'type:', type, 'pageNum:', pageNum, 'pageSize:', pageSize);
          
          // Filter model list based on parameters if needed
          let filteredModels = mockModelListData;
          
          // Calculate pagination
          const start = (pageNum - 1) * pageSize;
          const end = start + pageSize;
          const paginatedModels = filteredModels.slice(start, end);
          
          console.log('Pagination - start:', start, 'end:', end, 'paginated models count:', paginatedModels.length);
          
          await MockAPI.delay(500); // Simulate network delay
          const result = {
            success: true,
            data: {
              total: filteredModels.length,
              list: paginatedModels,
              pageNum: pageNum,
              pageSize: pageSize
            }
          };
          console.log('Returning result for /train/getTrainRecord:', result);
          return result;
        }
        
        // Handle local prediction list
        if (normalizedUrl.includes('/predict/getLocalPredictList')) {
          console.log('Handling /predict/getLocalPredictList request with params:', params);
          // Extract parameters
          const pageNum = params && params.pageNum ? parseInt(params.pageNum) : 1;
          const pageSize = params && params.pageSize ? parseInt(params.pageSize) : 10;
          const trainId = params && params.trainId;
          
          console.log('Extracted parameters - pageNum:', pageNum, 'pageSize:', pageSize, 'trainId:', trainId);
          
          // Filter predictions by trainId if provided
          let filteredPredictions = mockHistoricalPredictions;
          if (trainId) {
            // Find model name by trainId (model id)
            const model = mockModelListData.find(m => m.id === trainId);
            console.log('Looking for model with id:', trainId, 'Found model:', model);
            if (model) {
              filteredPredictions = mockHistoricalPredictions.filter(pred => pred.modelName === model.projectName);
              console.log('Filtered predictions by model name:', model.projectName, 'Result count:', filteredPredictions.length);
            } else {
              console.log('Model not found, using all predictions');
            }
          }
          
          // Calculate pagination
          const start = (pageNum - 1) * pageSize;
          const end = start + pageSize;
          const paginatedPredictions = filteredPredictions.slice(start, end);
          
          console.log('Pagination - start:', start, 'end:', end, 'paginated predictions count:', paginatedPredictions.length);
          
          await MockAPI.delay(500); // Simulate network delay
          const result = {
            success: true,
            data: {
              total: filteredPredictions.length,
              list: paginatedPredictions,
              pageNum: pageNum,
              pageSize: pageSize
            }
          };
          console.log('Returning result for /predict/getLocalPredictList:', result);
          return result;
        }
        
        // Handle file upload
        if (normalizedUrl.startsWith('/train/upload')) {
          await MockAPI.delay(1000); // Simulate upload time
          return {
            success: true,
            data: {
              // Return a mock file path
              filePath: `/mock/train/files/mock_file_${Date.now()}.csv`,
              fileName: params && params.file ? params.file.name : 'mock_file.csv',
              size: params && params.file ? params.file.size : 1024000
            }
          };
        }
        
        console.log('No specific mock handler for URL, using default behavior');
        await MockAPI.delay(200);
        return { data: { message: 'Success' } };
      } else {
        // When not using mock data, make real API calls with the original URL
        return await RealAPI.request('GET', url, params);
      }
    }
  },

  dateFormat: (timestamp) => {
    return new Date(timestamp).toLocaleString('zh-CN');
  },

  message: {
    success: (msg) => console.log('Success:', msg),
    error: (msg) => console.error('Error:', msg),
    warning: (msg) => console.warn('Warning:', msg)
  }
};



// Enhanced mock localStorage to support different users based on environment variables
const getMockLocalStorage = () => {
  // Default values
  const defaultLocalStorage = {
    authToken: 'mock_auth_token',
    authUserId: 'user_001',
    authUserName: 'Mock User',
    authRoleList: '1',
    authOrgId: 'org_001'
  };

  // Override with environment variable if set
  if (process.env.REACT_APP_MOCK_USER_ID) {
    const userId = process.env.REACT_APP_MOCK_USER_ID;
    const user = mockUsers.find(u => u.id === userId) || mockUsers[0];
    return {
      ...defaultLocalStorage,
      authUserId: user.id,
      authUserName: user.userName,
      authOrgId: user.orgId
    };
  }

  return defaultLocalStorage;
};

export { APIService, apiUtil, getMockLocalStorage, API_CONFIG };
